import {avatarsBucketUri} from '@features/utils/buckets';
import {captureException} from '@sentry/react';
import {z} from 'zod';

export function fixHtmlInClient(html: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  fixCssRoot(doc);
  replaceImageQueries(doc, html);
  replaceAvatars(doc, html);
  addLazyLoadingToImages(doc);

  return doc.documentElement.outerHTML;
}

function addLazyLoadingToImages(doc: Document) {
  doc
    .querySelectorAll<HTMLImageElement>('img:not([loading])')
    .forEach((img) => {
      img.setAttribute('loading', 'lazy');
    });
}

function fixCssRoot(doc: Document) {
  const styleTags = doc.querySelectorAll('style');

  styleTags.forEach((styleTag) => {
    let css = styleTag.textContent;

    css = css.replace(/:root\b/g, ':host');
    // Replace body selector with :host > div so body styles apply to the shadow DOM container
    // This ensures flex layouts and other body styles work correctly in the shadow DOM
    // Use negative lookbehind to avoid matching class names like .collapsible-body
    css = css.replace(/(?<![a-zA-Z0-9_-])body(?=\s*\{)/g, ':host > div');
    styleTag.textContent = css;
  });
}

function replaceImageQueries(doc: Document, rawHtml: string) {
  const images = doc.querySelectorAll<HTMLImageElement>('img[data-query]');
  images.forEach((img) => {
    const attr = img.getAttribute('data-query') ?? 'abstract gradient';
    const aspectRatio = img.getAttribute('data-aspect-ratio');

    // See imagesService.ts -> parseImage() for why we're using this weird format
    const prompt = aspectRatio ? `${aspectRatio}\n${attr}` : attr;

    // In this case, the LLM is mostly likely just generating our query right this second.
    // In that case, we simply bail (gracefully, by using the fallback img)
    if (rawHtml.endsWith(attr)) {
      img.src = '/image-fallback.png';
      return;
    }

    img.src = `/api/flow-image/${encodeURIComponent(prompt)}`;
  });
}

function replaceAvatars(doc: Document, rawHtml: string) {
  const images = doc.querySelectorAll<HTMLImageElement>('img[src^=avatar]');
  images.forEach((img) => {
    const attr = img.getAttribute('src') ?? 'avatar';

    // In this case, the LLM is mostly likely just generating our query right this second.
    // In that case, we simply bail (gracefully, by using the fallback img)
    if (rawHtml.endsWith(attr)) {
      img.src = '/image-fallback.png';
      return;
    }

    img.src = `${avatarsBucketUri}/${encodeURIComponent(attr)}`;
  });
}

function fixOldAvatars(doc: Document) {
  const oldImages = doc.querySelectorAll<HTMLImageElement>(
    'img[src$=".jpeg"],img[src$=".jpg"]',
  );
  oldImages.forEach((img) => {
    const src = img.getAttribute('src') ?? img.src;
    const isMatch = src.match(/^\/?avatar\d+\.(jpg|jpeg)$/);
    if (isMatch !== null) {
      img.src = new URL(src, 'https://app.banani.co').toString();
    } else {
      console.warn('pontential avatar image error?', img.src, src);
    }
  });
}

export async function replaceImageQueriesAndResolve(
  doc: Document,
  html: string,
  resolveImageQuery: (
    prompt: string,
  ) => Promise<string> = resolveImageQueryClient,
) {
  fixCssForExport(doc);

  const images = doc.querySelectorAll<HTMLImageElement>('img[data-query]');
  const imageQueriesPromise = Promise.allSettled(
    images.values().map(async (img) => {
      const attr = img.getAttribute('data-query') ?? 'abstract gradient';
      const aspectRatio = img.getAttribute('data-aspect-ratio');

      const prompt = aspectRatio ? `${aspectRatio}\n${attr}` : attr;

      img.src = await resolveImageQuery(prompt);
    }),
  );

  const styledElements = doc.querySelectorAll<HTMLElement>(
    '[style*="/api/flow-image/"]',
  );
  const inlineStylesPromise = Promise.allSettled(
    [...styledElements].map(async (el) => {
      const style = el.getAttribute('style');
      if (style === null) return;
      el.setAttribute(
        'style',
        await resolveFlowImageUrlsInText(style, resolveImageQuery),
      );
    }),
  );

  const styleTags = doc.querySelectorAll('style');
  const styleTagsPromise = Promise.allSettled(
    [...styleTags].map(async (tag) => {
      const css = tag.textContent;
      if (!css || !css.includes('/api/flow-image/')) return;
      tag.textContent = await resolveFlowImageUrlsInText(
        css,
        resolveImageQuery,
      );
    }),
  );

  await Promise.all([
    imageQueriesPromise,
    inlineStylesPromise,
    styleTagsPromise,
  ]);

  replaceAvatars(doc, html);

  fixOldAvatars(doc);

  return doc.documentElement.outerHTML;
}

const FLOW_IMAGE_URL_RE =
  /url\(\s*(['"]?)\/api\/flow-image\/([^)\s'"]+)\1\s*\)/g;

/**
 * Resolve `url(/api/flow-image/...)` references inside CSS text.
 * @internal Exported for testing only.
 */
export async function resolveFlowImageUrlsInText(
  text: string,
  resolveImageQuery: (prompt: string) => Promise<string>,
): Promise<string> {
  const matches = [...text.matchAll(FLOW_IMAGE_URL_RE)];
  if (matches.length === 0) return text;

  const resolutions = await Promise.allSettled(
    matches.map(async (m) => {
      const prompt = decodeURIComponent(m[2]);
      const url = await resolveImageQuery(prompt);
      return {original: m[0], url};
    }),
  );

  let result = text;
  for (const r of resolutions) {
    if (r.status !== 'fulfilled') continue;
    result = result.replace(r.value.original, () => `url(${r.value.url})`);
  }
  return result;
}

/**
 * Transform CSS for standalone export (outside Shadow DOM).
 * @internal Exported for testing only.
 */
export function transformCssForExport(css: string): string {
  // Replace :host > div with .export-wrapper FIRST
  // In shadow DOM, :host > div targets the wrapper div containing the content
  // In export, .export-wrapper is the same wrapper div
  css = css.replace(/:host\s*>\s*div/g, '.export-wrapper');

  // Replace remaining :host with :root for CSS variables
  // This handles cases like :host { --some-var: value; }
  // Must come after :host > div replacement to avoid converting it to :root > div
  css = css.replace(/:host\b/g, ':root');

  // Replace body selector with .export-wrapper class for exports
  // The export wrapper div will have this class applied
  // Use negative lookbehind to avoid matching class names like .collapsible-body
  css = css.replace(/(?<![a-zA-Z0-9_-])body(?=\s*\{)/g, '.export-wrapper');

  return css;
}

function fixCssForExport(doc: Document) {
  const styleTags = doc.querySelectorAll('style');

  styleTags.forEach((styleTag) => {
    const css = styleTag.textContent;
    if (!css) return;
    styleTag.textContent = transformCssForExport(css);
  });
}

async function resolveImageQueryClient(attr: string) {
  try {
    const res = await fetch(`/api/flow-image/${encodeURIComponent(attr)}?json`);

    if (!res.ok) {
      captureException(new Error(`Could not resolve image - invalid status.`));
      return 'https://app.banani.co/image-fallback.png';
    }

    return z.object({url: z.url()}).parse(await res.json()).url;
  } catch (e) {
    captureException(e);
    return 'https://app.banani.co/image-fallback.png';
  }
}
